import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { devError } from "../lib/devLog";
import { Trash2, Pencil, X } from "lucide-react";
import { formatPrice } from "../lib/format";
import MediaUploader from "../components/MediaUploader";
import SiteSettingsAdmin from "../components/SiteSettingsAdmin";
import EmailTemplatesAdmin from "../components/admin/EmailTemplatesAdmin";
import BroadcastsAdmin from "../components/admin/BroadcastsAdmin";
import CustomersAdmin from "../components/admin/CustomersAdmin";
import EngagementAdmin from "../components/admin/EngagementAdmin";
import AdminDashboard from "../components/admin/AdminDashboard";
import AdminOrders from "../components/admin/AdminOrders";
import AboutPageAdmin from "../components/admin/AboutPageAdmin";

const TABS = ["dashboard", "orders", "customers", "engagement", "broadcasts", "settings", "about", "emails", "products", "categories", "coupons", "messages"];

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [openOrderId, setOpenOrderId] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/login");
  }, [user, loading, nav]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto">
      <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Atelier</p>
      <h1 className="font-serif text-5xl md:text-6xl mb-12">Admin Console</h1>

      <div className="flex gap-2 border-b border-[rgba(244,244,240,0.06)] mb-10 overflow-x-auto">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-3 text-xs uppercase tracking-[0.22em] ${tab === t ? "text-gold border-b border-[#C8A97E]" : "text-muted hover:text-gold"}`}
            data-testid={`admin-tab-${t}`}
          >{t}</button>
        ))}
      </div>

      {tab === "dashboard" && <AdminDashboard onOpenOrder={(id) => { setOpenOrderId(id); setTab("orders"); }} />}
      {tab === "orders" && <AdminOrders openOrderId={openOrderId} onClose={() => setOpenOrderId(null)} />}
      {tab === "settings" && <SiteSettingsAdmin />}
      {tab === "about" && <AboutPageAdmin />}
      {tab === "emails" && <EmailTemplatesAdmin />}
      {tab === "broadcasts" && <BroadcastsAdmin />}
      {tab === "customers" && <CustomersAdmin />}
      {tab === "engagement" && <EngagementAdmin />}
      {tab === "products" && <Products />}
      {tab === "categories" && <Categories />}
      {tab === "coupons" && <Coupons />}
      {tab === "messages" && <Messages />}
    </div>
  );
}

function Products() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const empty = { name: "", price: 100, description: "", category: "", sizes: ["XS","S","M","L"], images: [], videos: [], featured: false, stock: 50 };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null); // { product }
  const refresh = useCallback(() => api.get("/products").then(({ data }) => setItems(data)), []);
  const refreshCats = useCallback(() => api.get("/categories?include_hidden=true").then(({ data }) => setCategories(data)).catch(() => {}), []);
  useEffect(() => { refresh(); refreshCats(); }, [refresh, refreshCats]);

  // Once categories load, pre-select the first visible one on the create form (if not chosen)
  useEffect(() => {
    if (!form.category && categories.length) {
      const first = categories.find(c => !c.hidden) || categories[0];
      if (first) setForm(f => ({ ...f, category: first.name }));
    }
  }, [categories, form.category]);

  const create = async (e) => {
    e.preventDefault();
    if (form.images.length === 0) {
      toast.error("Please upload at least one image");
      return;
    }
    try {
      await api.post("/products", { ...form, price: Number(form.price) });
      toast.success("Product created");
      setForm(empty);
      refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (pid) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/products/${pid}`);
    refresh();
  };

  const startEdit = (p) => {
    setEditing({
      product: {
        ...p,
        sizes: p.sizes || ["XS","S","M","L"],
        images: p.images || [],
        videos: p.videos || [],
      },
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const { product } = editing;
    try {
      await api.put(`/products/${product.product_id}`, {
        name: product.name,
        price: Number(product.price),
        description: product.description || "",
        category: product.category || "",
        sizes: product.sizes || [],
        images: product.images || [],
        videos: product.videos || [],
        featured: !!product.featured,
        stock: Number(product.stock) || 0,
      });
      toast.success("Product updated");
      setEditing(null);
      refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const setEditField = (k, v) => setEditing(e => ({ ...e, product: { ...e.product, [k]: v } }));

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <form onSubmit={create} className="space-y-5 bg-[#141414] p-8" data-testid="product-form">
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold">New Product</h3>
        <input className="input-line" placeholder="Name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
        <div className="grid grid-cols-2 gap-4">
          <input className="input-line" type="number" placeholder="Price (₹)" required value={form.price} onChange={e => setForm({...form, price: e.target.value})} />
          <input className="input-line" type="number" min="0" placeholder="Stock qty" required value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} data-testid="create-stock" />
        </div>
        <textarea className="input-line resize-none" rows={3} placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
        <CategorySelect
          categories={categories}
          value={form.category}
          onChange={(v) => setForm({...form, category: v})}
          testid="create-category"
        />
        <SizePicker
          value={form.sizes}
          onChange={(v) => setForm({...form, sizes: v})}
          testidPrefix="create-size"
        />
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-muted block mb-2">Media</label>
          <MediaUploader
            images={form.images}
            videos={form.videos}
            onChange={({ images, videos }) => setForm({ ...form, images, videos })}
          />
        </div>
        <label className="flex items-center gap-3 text-sm text-muted"><input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} /> Featured</label>
        <button className="btn-gold" data-testid="create-product-btn">Create Product</button>
      </form>

      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-5">All Products ({items.length})</h3>
        <ul className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
          {items.map(p => (
            <li key={p.product_id} className="flex gap-4 bg-[#141414] p-3 items-center" data-testid={`admin-product-${p.product_id}`}>
              <img src={p.images?.[0]} alt="" className="w-16 h-20 object-cover" />
              <div className="flex-1">
                <p className="font-serif text-base">{p.name}</p>
                <p className="text-xs text-muted">
                  {p.category} · {formatPrice(p.price)} · Stock <span className={p.stock <= 0 ? "text-red-400 font-medium" : p.stock <= 5 ? "text-gold" : "text-text"}>{p.stock ?? 0}</span> · {p.images?.length || 0} img{(p.videos?.length || 0) > 0 ? ` · ${p.videos.length} vid` : ""}
                </p>
              </div>
              <button onClick={() => startEdit(p)} className="text-muted hover:text-gold p-2" data-testid={`edit-product-${p.product_id}`}><Pencil size={16} strokeWidth={1.5} /></button>
              <button onClick={() => remove(p.product_id)} className="text-muted hover:text-gold p-2"><Trash2 size={16} strokeWidth={1.5} /></button>
            </li>
          ))}
        </ul>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={saveEdit} className="relative bg-[#0C0C0C] border border-[rgba(244,244,240,0.08)] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 space-y-5" data-testid="edit-product-form">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl">Edit Product</h3>
              <button type="button" onClick={() => setEditing(null)} className="text-muted hover:text-gold"><X size={20} strokeWidth={1.5} /></button>
            </div>
            <input className="input-line" placeholder="Name" required value={editing.product.name} onChange={e => setEditField("name", e.target.value)} data-testid="edit-name" />
            <input className="input-line" type="number" placeholder="Price (₹)" required value={editing.product.price} onChange={e => setEditField("price", e.target.value)} data-testid="edit-price" />
            <textarea className="input-line resize-none" rows={3} placeholder="Description" value={editing.product.description || ""} onChange={e => setEditField("description", e.target.value)} />
            <CategorySelect
              categories={categories}
              value={editing.product.category || ""}
              onChange={(v) => setEditField("category", v)}
              testid="edit-category"
            />
            <SizePicker
              value={editing.product.sizes || []}
              onChange={(v) => setEditField("sizes", v)}
              testidPrefix="edit-size"
            />
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted block mb-2">Media</label>
              <MediaUploader
                images={editing.product.images || []}
                videos={editing.product.videos || []}
                onChange={({ images, videos }) => setEditing(s => ({ ...s, product: { ...s.product, images, videos } }))}
              />
            </div>
            <input className="input-line" type="number" min="0" placeholder="Stock" value={editing.product.stock ?? 0} onChange={e => setEditField("stock", e.target.value)} data-testid="edit-stock" />
            <label className="flex items-center gap-3 text-sm text-muted">
              <input type="checkbox" checked={!!editing.product.featured} onChange={e => setEditField("featured", e.target.checked)} /> Featured
            </label>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="btn-gold flex-1" data-testid="save-product-btn">Save Changes</button>
              <button type="button" className="btn-outline" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Standard clothing sizes for a multi-select picker. Admin can also add custom
// tags like "Free Size" or "XS-M" for pieces that don't fit the standard set.
const STANDARD_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "Free Size"];

function SizePicker({ value = [], onChange, testidPrefix = "size" }) {
  const [custom, setCustom] = useState("");
  const has = (s) => value.includes(s);
  const toggle = (s) => {
    if (has(s)) onChange(value.filter(x => x !== s));
    else onChange([...value, s]);
  };
  const addCustom = () => {
    const clean = custom.trim().toUpperCase().replace(/\s+/g, " ");
    if (!clean) return;
    if (value.includes(clean)) { toast.message(`"${clean}" already added`); setCustom(""); return; }
    if (clean.length > 20) { toast.error("Size label too long (max 20 chars)"); return; }
    onChange([...value, clean]);
    setCustom("");
  };
  // Non-standard sizes already in the value list (custom tags)
  const customTags = value.filter(v => !STANDARD_SIZES.includes(v));

  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2">Available sizes</label>
      <div className="flex flex-wrap gap-2 mb-3" data-testid={`${testidPrefix}-picker`}>
        {STANDARD_SIZES.map(s => (
          <button
            key={s}
            type="button"
            onClick={() => toggle(s)}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border transition-colors ${has(s) ? "bg-gold text-black border-gold" : "border-[rgba(244,244,240,0.15)] text-muted hover:border-gold hover:text-gold"}`}
            data-testid={`${testidPrefix}-${s.replace(/\s+/g,'_')}`}
          >{s}</button>
        ))}
        {customTags.map(t => (
          <span
            key={t}
            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border bg-gold/10 text-gold border-gold/30 flex items-center gap-2"
            data-testid={`${testidPrefix}-custom-${t.replace(/\s+/g,'_')}`}
          >
            {t}
            <button type="button" onClick={() => toggle(t)} className="hover:text-red-400" title={`Remove ${t}`}>×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          className="input-line flex-1 !py-1.5 text-sm"
          placeholder="Add custom (e.g. XS-M, One Size, EU 36)"
          value={custom}
          onChange={e => setCustom(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          maxLength={20}
          data-testid={`${testidPrefix}-custom-input`}
        />
        <button type="button" onClick={addCustom} className="btn-outline !px-3 !py-1.5 text-[10px]" data-testid={`${testidPrefix}-custom-add`}>Add</button>
      </div>
      <p className="text-[10px] text-muted/70 mt-1.5">Tip: use custom tags for free-size pieces (e.g. "XS-M") — shown to customers exactly as typed.</p>
    </div>
  );
}

function CategorySelect({ categories, value, onChange, testid = "category-select" }) {
  const visible = (categories || []).filter(c => !c.hidden);
  const namesInList = visible.map(c => c.name);
  const valueIsMissing = value && !namesInList.includes(value);
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2">Category</label>
      <select
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        className="input-line !bg-transparent"
        data-testid={testid}
        required
      >
        <option value="" disabled className="bg-[#0C0C0C]">Select a category…</option>
        {valueIsMissing && (
          <option value={value} className="bg-[#0C0C0C]">{value} (hidden or removed)</option>
        )}
        {visible.map(c => (
          <option key={c.category_id} value={c.name} className="bg-[#0C0C0C]">{c.name}</option>
        ))}
      </select>
      {visible.length === 0 && (
        <p className="text-[10px] text-red-400 mt-1.5">No categories yet — add one from the Categories tab.</p>
      )}
    </div>
  );
}

function Categories() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/categories?include_hidden=true");
      setItems(data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const nextOrder = (items[items.length - 1]?.order || items.length * 10) + 10;
      await api.post("/admin/categories", { name: name.trim(), order: nextOrder });
      toast.success("Category added");
      setName("");
      refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const toggleHidden = async (c) => {
    try {
      await api.put(`/admin/categories/${c.category_id}`, { name: c.name, hidden: !c.hidden, order: c.order });
      refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (c) => {
    if (!confirm(`Remove "${c.name}"? This can only be done if no products use it.`)) return;
    try {
      await api.delete(`/admin/categories/${c.category_id}`);
      toast.success("Category removed");
      refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const saveRename = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/categories/${editing.category_id}`, {
        name: editing.name.trim(),
        hidden: !!editing.hidden,
        order: Number(editing.order) || 0,
      });
      toast.success("Category updated");
      setEditing(null);
      refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <form onSubmit={create} className="bg-[#141414] p-8 space-y-4" data-testid="category-form">
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold">New Category</h3>
        <input
          className="input-line"
          placeholder="e.g. Sarees, Lounge, Accessories"
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={60}
          required
          data-testid="new-category-name"
        />
        <p className="text-[10px] text-muted/70">Category names show up in the site header, shop filters, and product form. You can hide a category to remove it from customer view without deleting it.</p>
        <button className="btn-gold" data-testid="create-category-btn">Add Category</button>
      </form>

      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-5">All Categories ({items.length})</h3>
        <ul className="space-y-2">
          {items.map(c => (
            <li key={c.category_id} className="bg-[#141414] p-4 flex items-center justify-between gap-3" data-testid={`category-row-${c.category_id}`}>
              <div className="min-w-0">
                <p className={`font-serif ${c.hidden ? "text-muted line-through" : "text-text"}`}>{c.name}</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted mt-1">
                  slug: {c.slug} · order {c.order}{c.hidden ? " · hidden" : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleHidden(c)}
                  className="text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold border border-[rgba(244,244,240,0.1)] px-2 py-1"
                  data-testid={`toggle-hidden-${c.category_id}`}
                  title={c.hidden ? "Show to customers" : "Hide from customers"}
                >{c.hidden ? "Show" : "Hide"}</button>
                <button onClick={() => setEditing({ ...c })} className="text-muted hover:text-gold p-1.5" data-testid={`edit-category-${c.category_id}`} title="Rename"><Pencil size={14} strokeWidth={1.5} /></button>
                <button onClick={() => remove(c)} className="text-muted hover:text-red-400 p-1.5" data-testid={`delete-category-${c.category_id}`} title="Delete"><Trash2 size={14} strokeWidth={1.5} /></button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={saveRename} className="relative bg-[#0C0C0C] border border-[rgba(244,244,240,0.08)] w-full max-w-md p-8 space-y-4" data-testid="edit-category-modal">
            <h3 className="font-serif text-2xl">Edit Category</h3>
            <input className="input-line" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} required data-testid="edit-category-name" />
            <div className="grid grid-cols-2 gap-4">
              <input className="input-line" type="number" placeholder="Order" value={editing.order} onChange={e => setEditing({ ...editing, order: e.target.value })} />
              <label className="flex items-center gap-2 text-xs text-muted">
                <input type="checkbox" checked={!!editing.hidden} onChange={e => setEditing({ ...editing, hidden: e.target.checked })} />
                Hidden
              </label>
            </div>
            <p className="text-[10px] text-muted/70">Renaming will automatically update the category on all products currently using it.</p>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="btn-gold flex-1" data-testid="save-category-btn">Save</button>
              <button type="button" onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}



function Coupons() {
  const [items, setItems] = useState([]);
  const empty = {
    code: "", type: "percent", discount_percent: 10, flat_off: 0, buy_qty: 2, get_qty: 1,
    active: true, auto_apply: true, min_order: 0, usage_limit: 0,
    once_per_user: false, first_order_only: false, expires_at: "", label: "",
  };
  const [form, setForm] = useState(empty);
  const refresh = useCallback(() => api.get("/coupons").then(({ data }) => setItems(data)).catch((e) => devError(e)), []);
  useEffect(() => { refresh(); }, [refresh]);
  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: form.code.toUpperCase(),
        type: form.type,
        active: form.active,
        auto_apply: !!form.auto_apply,
        min_order: Number(form.min_order) || 0,
        usage_limit: Number(form.usage_limit) || 0,
        once_per_user: !!form.once_per_user,
        first_order_only: !!form.first_order_only,
        expires_at: form.expires_at || null,
        label: (form.label || "").trim() || null,
      };
      if (form.type === "percent") payload.discount_percent = Number(form.discount_percent) || 0;
      if (form.type === "flat_off") payload.flat_off = Number(form.flat_off) || 0;
      if (form.type === "bxgy") { payload.buy_qty = Number(form.buy_qty) || 0; payload.get_qty = Number(form.get_qty) || 0; }
      await api.post("/coupons", payload);
      toast.success("Coupon created"); setForm(empty); refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };
  const remove = async (cid) => { await api.delete(`/coupons/${cid}`); refresh(); };
  const toggleShow = async (c) => {
    try {
      await api.patch(`/coupons/${c.coupon_id}`, { auto_apply: !c.auto_apply });
      refresh();
    } catch (e) { devError(e); }
  };

  const summarize = (c) => {
    const t = (c.type || "percent").toLowerCase();
    if (t === "bxgy") return `Buy ${c.buy_qty} Get ${c.get_qty} Free`;
    if (t === "flat_off") return `₹${c.flat_off} off`;
    return `${c.discount_percent}% off`;
  };

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <form onSubmit={create} className="bg-[#141414] p-8 space-y-5" data-testid="coupon-form">
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold">New Coupon</h3>
        <input className="input-line uppercase" placeholder="CODE" required value={form.code} onChange={e => setForm({...form, code: e.target.value})} />

        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Coupon Type</label>
          <select className="input-line !bg-transparent" value={form.type} onChange={e => setForm({...form, type: e.target.value})} data-testid="coupon-type">
            <option value="percent" className="bg-[#0C0C0C]">Percentage off (e.g. 10%)</option>
            <option value="flat_off" className="bg-[#0C0C0C]">Flat amount off (e.g. ₹100)</option>
            <option value="bxgy" className="bg-[#0C0C0C]">Buy X Get Y (cheapest free)</option>
          </select>
        </div>

        {form.type === "percent" && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Discount %</label>
            <input className="input-line" type="number" min="1" max="100" required value={form.discount_percent} onChange={e => setForm({...form, discount_percent: e.target.value})} data-testid="coupon-discount-percent" />
          </div>
        )}
        {form.type === "flat_off" && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Flat off (₹)</label>
            <input className="input-line" type="number" min="1" required value={form.flat_off} onChange={e => setForm({...form, flat_off: e.target.value})} data-testid="coupon-flat-off" />
          </div>
        )}
        {form.type === "bxgy" && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Buy quantity</label>
              <input className="input-line" type="number" min="1" required value={form.buy_qty} onChange={e => setForm({...form, buy_qty: e.target.value})} data-testid="coupon-buy-qty" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Get free quantity</label>
              <input className="input-line" type="number" min="1" required value={form.get_qty} onChange={e => setForm({...form, get_qty: e.target.value})} data-testid="coupon-get-qty" />
            </div>
            <p className="col-span-2 text-[10px] text-muted/80">The <span className="text-gold">cheapest</span> item(s) in each qualifying group become free — customer-friendly Shopify-style behaviour.</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Min Order (₹)</label>
            <input className="input-line" type="number" min="0" value={form.min_order} onChange={e => setForm({...form, min_order: e.target.value})} data-testid="coupon-min-order" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Usage Limit (0 = ∞)</label>
            <input className="input-line" type="number" min="0" value={form.usage_limit} onChange={e => setForm({...form, usage_limit: e.target.value})} data-testid="coupon-usage-limit" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Expires</label>
            <input className="input-line" type="date" value={form.expires_at} onChange={e => setForm({...form, expires_at: e.target.value})} data-testid="coupon-expires" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Short label</label>
            <input className="input-line" type="text" placeholder="e.g. Buy 2 Get 1 Free" value={form.label} onChange={e => setForm({...form, label: e.target.value})} data-testid="coupon-label" />
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-muted">
            <input type="checkbox" checked={form.auto_apply} onChange={e => setForm({...form, auto_apply: e.target.checked})} data-testid="coupon-auto-apply" /> Show in cart &amp; checkout picker
          </label>
          <label className="flex items-center gap-2 text-sm text-muted">
            <input type="checkbox" checked={form.first_order_only} onChange={e => setForm({...form, first_order_only: e.target.checked})} data-testid="coupon-first-order" /> First order only
          </label>
          <label className="flex items-center gap-2 text-sm text-muted">
            <input type="checkbox" checked={form.once_per_user} onChange={e => setForm({...form, once_per_user: e.target.checked})} data-testid="coupon-once-per-user" /> One use per customer
          </label>
        </div>

        <button type="submit" data-testid="create-coupon-btn" className="btn-gold">Create Coupon</button>
      </form>
      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-5">All Coupons</h3>
        <ul className="space-y-3">
          {items.map(c => (
            <li key={c.coupon_id} className="bg-[#141414] p-5 flex justify-between items-start">
              <div className="flex-1">
                <p className="font-serif text-lg text-gold tracking-[0.15em]">{c.code}</p>
                <p className="text-xs text-muted mt-1">{summarize(c)} · {c.active ? "Active" : "Inactive"}</p>
                <div className="flex flex-wrap gap-2 mt-2 text-[10px] uppercase tracking-[0.18em] text-muted/70">
                  {c.auto_apply && <span className="text-gold">Auto-apply</span>}
                  {c.min_order > 0 && <span>Min ₹{c.min_order}</span>}
                  {c.usage_limit > 0 && <span>Limit {c.usage_limit}</span>}
                  {c.first_order_only && <span className="text-gold">First Order Only</span>}
                  {c.once_per_user && <span>1 / customer</span>}
                  {c.expires_at && <span>Until {String(c.expires_at).slice(0,10)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleShow(c)}
                  className={`text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${c.auto_apply ? "border-[#C8A97E] text-gold hover:bg-[rgba(200,169,126,0.08)]" : "border-[rgba(244,244,240,0.15)] text-muted hover:text-gold hover:border-[#C8A97E]"}`}
                  data-testid={`toggle-show-${c.coupon_id}`}
                  title={c.auto_apply ? "Currently shown in cart & checkout — click to hide" : "Currently hidden from picker — click to show"}
                >
                  {c.auto_apply ? "Shown" : "Hidden"}
                </button>
                <button onClick={() => remove(c.coupon_id)} className="text-muted hover:text-gold p-2" data-testid={`delete-coupon-${c.coupon_id}`}><Trash2 size={16} strokeWidth={1.5} /></button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Messages() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/contact").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  return (
    <div>
      <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-5">Contact Messages ({items.length})</h3>
      <ul className="space-y-4">
        {items.map(m => (
          <li key={m.contact_id} className="bg-[#141414] p-6">
            <div className="flex justify-between mb-2">
              <p className="font-serif text-lg">{m.name}</p>
              <p className="text-xs text-muted">{new Date(m.created_at).toLocaleString()}</p>
            </div>
            <p className="text-xs text-gold mb-3">{m.email}</p>
            <p className="text-muted leading-relaxed">{m.message}</p>
          </li>
        ))}
        {items.length === 0 && <p className="text-muted">No messages yet.</p>}
      </ul>
    </div>
  );
}
